import React from "react";
import { Field } from "react-final-form";
import { withApollo } from "react-apollo";
import { SignedUploader, UploadField } from "@navjobs/upload";
import { VictoryPie } from "victory-native";
import { withTheme } from "styled-components/native";
import Icon from "../icon/FontAwesomePro";
import { getUploadTokenQuery } from "../query/Uploader";
import FormImageField from "./FormImageField";

/**
 * Previews and allows uploading a new image from the user's desktop computer.
 *
 * Once an image is selected, upload is started immediately and the upload token sent by the server is updated in the form once the upload has completed.
 * When this happens, the form can be saved along with the rest of the information.
 *
 * This component is device specific and only works on the browser.
 *
 * Props:
 * name: Form field where the upload token will be saved once the upload has completed.
 * size: Width and height (in pixels) of the image.
 * border: Size of the border (in pixels).
 * entityName: Name of the entity related to the storage of the file. This is used to name the directories in Amazon. For example, putting "Contact" here would save the file in "Contact/12345-43455-453454-444/original".
 * templateIndex: This is used for the view of the image uploaded:
 *                entityName == Contact:   templateIndex=0 (default)
 *                entityName == Attribute: templateIndex=1
 *                entityName == Verifier:  templateIndex=2
 * placeholder: Image when there is no photo (as a placeholder). By default is used the png on ../assets/images/placeholder.png. You can hide any placeholder with theme.field.uploadImage.usePlaceholder = false.
 * onUploaded: Function called when the file has been successfully uploaded to Amazon.
 */
class FormUploadImageField extends React.PureComponent {
  constructor() {
    super();

    this.state = { imageSrc: null };
  }

  getData(percent) {
    return [{ x: 1, y: percent }, { x: 2, y: 100 - percent }];
  }

  render() {
    const {
      client,
      theme,
      size,
      border,
      entityName,
      onUploaded,
      ...restProps
    } = this.props,
    me = this;

    return (
      <FormImageField {...restProps} src={this.state.imageSrc} size={size}>
        {({ input }) => (
          <SignedUploader
            beforeRequest={({ files }) => {
              //Preview the image before it is uploaded
              let reader = new FileReader();

              reader.onload = function(e) {
                //Set the state so that the selected image is previewed immediately
                me.setState({ imageSrc: e.target.result });
              };

              reader.readAsDataURL(files[0]);
              //End of Preview the image before it is uploaded

              //Upload token is requested from server with GraphQL
              return client.query({
                query: getUploadTokenQuery,
                //Ensures that a token is always generated in the server, otherwise Apollo will return a previous token
                fetchPolicy: "no-cache",
                variables: { entityName }
              });
            }}
            //Request that uploads file to presigned URL sent from server
            request={({ before, files }) => ({
              url: before.data.getUploadToken.uploadUrl,
              method: "PUT",
              withCredentials: false
            })}
            afterRequest={({ before, status }) =>
              new Promise(resolve => {
                //Update the Final Form field so that the new token gets submitted with the form
                input.onChange(before.data.getUploadToken.token);
                onUploaded();
                resolve(true);
              })
            }
            uploadOnSelection={true}
          >
            {({ onFiles, progress, complete }) => (
              // This is the clickable field that allows selecting a file
              <UploadField
                onFiles={onFiles}
                uploadProps={{
                  accept: '.doc,.docx,.pdf',
                }}
              >
                {/* Provides the circular upload progress bar around the image */}
                <VictoryPie
                  innerRadius={(size - border) / 2}
                  width={size}
                  height={size}
                  padding={0}
                  data={this.getData(progress || 0)}
                  colorScale={[
                    theme.fontColor,
                    theme.secondaryColor
                  ]}
                  // Disables the labels
                  labels={() => null}
                />

                {/* Camera icon that appears along the bottom right of the image */}
                <Icon
                  name="file-search"
                  iconSize={25}
                  weight={900}
                  color={theme.fontColor}
                  style={{
                    position: "absolute",
                    bottom: 13,
                    right: 13,
                    height: 25,
                    width: 25
                  }}
                />
              </UploadField>
            )}
          </SignedUploader>
        )}
      </FormImageField>
    );
  }
}

FormUploadImageField.defaultProps = {
  onUploaded: () => {}
};

export default withApollo(withTheme(FormUploadImageField));
